import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Wide Skyscraper Ad Placeholder - Gray style matching the design reference
const AdPlaceholder = ({ position, size = "160x400" }) => {
  return (
    <div className="bg-[#9ca3af] rounded-xl p-3 flex flex-col items-center justify-center text-center min-h-[320px]">
      <span className="text-white font-bold text-xs uppercase tracking-wide mb-0.5">
        AMAMAZA HANO!
      </span>
      <span className="text-white/80 text-[10px] uppercase mb-4 leading-tight">
        KANDA AHA UMENYE<br />UKO WABIGENZA
      </span>
      
      <span className="text-gray-700 font-medium text-xs mb-0.5">
        Wide Skyscraper
      </span>
      <span className="text-gray-600 text-[10px] mb-4 leading-tight">
        Ads Banner<br />{size}
      </span>
      
      <span className="text-white font-bold text-[10px] uppercase tracking-wide leading-tight">
        ADVERTISE<br />WITH US NOW!
      </span>
      <a 
        href="mailto:ads@yegonews.com"
        className="text-white text-[10px] uppercase hover:underline mt-1"
      >
        CLICK FOR MORE
      </a>
    </div>
  );
};

// Dynamic Partner/Direct Ad Slot Component with Video/GIF support
const PartnerAdSlot = ({ ad, onClose, showPlaceholder = true, position = "sidebar" }) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isTextVisible, setIsTextVisible] = useState(true);
  const videoRef = useRef(null);

  // Track impression
  useEffect(() => {
    if (ad && ad.id) {
      axios.post(`${BACKEND_URL}/api/ads/${ad.id}/impression`).catch(() => {});
    }
  }, [ad]);

  // Rotate text every N seconds
  useEffect(() => {
    if (!ad?.rotating_texts || ad.rotating_texts.length <= 1) return;

    const interval = (ad.text_interval || 3) * 1000;
    
    const timer = setInterval(() => {
      setIsTextVisible(false);
      setTimeout(() => {
        setCurrentTextIndex(prev => (prev + 1) % ad.rotating_texts.length);
        setIsTextVisible(true);
      }, 300);
    }, interval);

    return () => clearInterval(timer);
  }, [ad?.rotating_texts, ad?.text_interval]);

  const handleClick = () => {
    if (ad && ad.id) {
      axios.post(`${BACKEND_URL}/api/ads/${ad.id}/click`).catch(() => {});
    }
  };

  // Show placeholder if no ad
  if (!ad) {
    if (!showPlaceholder) return null;
    return <AdPlaceholder position={position} />;
  }

  // Determine media type
  const hasVideo = ad.video_url && (
    ad.video_url.endsWith('.mp4') || 
    ad.video_url.endsWith('.webm') || 
    ad.video_url.includes('video')
  );
  const hasGif = ad.image_url && ad.image_url.endsWith('.gif');
  const hasImage = ad.image_url;
  const hasRotatingText = ad.rotating_texts && ad.rotating_texts.length > 0;
  const hasCTA = ad.cta_text;
  const overlayStyle = ad.overlay_color ? { backgroundColor: ad.overlay_color } : {};

  // CTA button styles
  const ctaStyles = {
    default: "bg-white text-gray-900 hover:bg-gray-100",
    primary: "bg-emerald-500 text-white hover:bg-emerald-600",
    outline: "bg-transparent border-2 border-white text-white hover:bg-white/20",
    red: "bg-red-500 text-white hover:bg-red-600",
    dark: "bg-gray-900 text-white hover:bg-gray-800"
  };

  return (
    <div className="relative bg-white dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800">
      {/* Ad Label */}
      <div className="absolute top-2 left-2 z-20">
        <span className="px-2 py-0.5 bg-black/60 text-white text-[10px] rounded uppercase tracking-wider font-medium">
          Ad
        </span>
      </div>
      
      {/* Close button for admins */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-20 p-1 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
        >
          <X className="h-3 w-3" />
        </button>
      )}
      
      {/* Ad Content */}
      <a 
        href={ad.link || '#'} 
        target="_blank" 
        rel="noopener noreferrer"
        className="block relative"
        onClick={handleClick}
      >
        {/* Media Container */}
        <div className="relative">
          {/* Video Background (MP4/WebM) */}
          {hasVideo && (
            <video
              ref={videoRef}
              src={ad.video_url}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-auto object-cover"
              style={{ minHeight: '150px' }}
            />
          )}

          {/* GIF or Static Image */}
          {!hasVideo && hasImage && (
            <img 
              src={ad.image_url} 
              alt={ad.title || 'Advertisement'}
              className="w-full h-auto object-cover"
            />
          )}

          {/* Fallback gradient if no media */}
          {!hasVideo && !hasImage && (
            <div className="min-h-[200px] bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600" />
          )}

          {/* Content Overlay - Shows on video/gradient backgrounds or when we have rotating text/CTA */}
          {(hasVideo || !hasImage || hasRotatingText || hasCTA) && (
            <div 
              className="absolute inset-0 flex flex-col items-center justify-center p-4"
              style={overlayStyle}
            >
              {/* Title - Only show if no rotating text */}
              {ad.title && !hasRotatingText && (hasVideo || !hasImage) && (
                <h3 className="text-white font-bold text-lg text-center drop-shadow-lg mb-1">
                  {ad.title}
                </h3>
              )}

              {/* Static Description */}
              {ad.description && !hasRotatingText && (hasVideo || !hasImage) && (
                <p className="text-white/90 text-sm text-center drop-shadow-md">
                  {ad.description}
                </p>
              )}

              {/* Rotating Text */}
              {hasRotatingText && (
                <div className="text-center min-h-[60px] flex items-center justify-center">
                  <p 
                    className={`text-white font-semibold text-base drop-shadow-lg transition-opacity duration-300 ${
                      isTextVisible ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {ad.rotating_texts[currentTextIndex]}
                  </p>
                </div>
              )}

              {/* Call to Action Button */}
              {hasCTA && (
                <button 
                  className={`mt-3 px-5 py-2 rounded-full text-sm font-semibold transition-all transform hover:scale-105 shadow-lg ${
                    ctaStyles[ad.cta_style] || ctaStyles.default
                  }`}
                >
                  {ad.cta_text}
                  <ExternalLink className="inline-block ml-1.5 h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
      </a>
      
      {/* Partner name */}
      {ad.partner_name && (
        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Sponsored by {ad.partner_name}
          </span>
        </div>
      )}
    </div>
  );
};

const RightSidebar = () => {
  const [adsData, setAdsData] = useState({ ads_enabled: false, positions: {} });
  const [loading, setLoading] = useState(true);

  // Fetch ads data including global toggle status
  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/ads/all`);
        setAdsData(response.data);
      } catch (error) {
        console.log('Failed to fetch ads');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAds();
  }, []);

  // Get ad for specific position
  const getAdForPosition = (position) => {
    if (!adsData.positions) return null;
    const ads = adsData.positions[position] || [];
    return ads.length > 0 ? ads[0] : null;
  };

  // Loading state
  if (loading) {
    return (
      <aside className="hidden xl:block w-44 flex-shrink-0">
        <div className="space-y-4">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20 mx-auto mb-4"></div>
            <div className="h-[350px] bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
          </div>
        </div>
      </aside>
    );
  }

  // Always show sidebar - with ads or placeholders flowing down the page
  return (
    <aside className="hidden xl:block w-44 flex-shrink-0">
      <div className="space-y-4">
        {/* Kwamamaza Header */}
        <div className="text-center">
          <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest">
            Kwamamaza
          </span>
        </div>
        
        {/* Ad Slot 1 */}
        <PartnerAdSlot 
          ad={getAdForPosition('sidebar-1')} 
          showPlaceholder={true}
          position="sidebar-1" 
        />
        
        {/* Ad Slot 2 */}
        <PartnerAdSlot 
          ad={getAdForPosition('sidebar-2')} 
          showPlaceholder={true}
          position="sidebar-2" 
        />
        
        {/* Ad Slot 3 */}
        <PartnerAdSlot 
          ad={getAdForPosition('sidebar-3')} 
          showPlaceholder={true}
          position="sidebar-3" 
        />
        
        {/* Ad Slot 4 */}
        <PartnerAdSlot 
          ad={getAdForPosition('sidebar-4')} 
          showPlaceholder={true}
          position="sidebar-4" 
        />
        
        {/* Ad Slot 5 */}
        <PartnerAdSlot 
          ad={getAdForPosition('sidebar-5')} 
          showPlaceholder={true}
          position="sidebar-5" 
        />
        
        {/* Ad Slot 6 */}
        <PartnerAdSlot 
          ad={getAdForPosition('sidebar-6')} 
          showPlaceholder={true}
          position="sidebar-6" 
        />
        
        {/* Ad Slot 7 */}
        <PartnerAdSlot 
          ad={getAdForPosition('sidebar-7')} 
          showPlaceholder={true}
          position="sidebar-7" 
        />
        
        {/* Ad Slot 8 */}
        <PartnerAdSlot 
          ad={getAdForPosition('sidebar-8')} 
          showPlaceholder={true}
          position="sidebar-8" 
        />
      </div>
    </aside>
  );
};

export default RightSidebar;
